import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import {
  Form,
  HttpError,
  Identifier,
  minValue,
  NumberInput,
  RaRecord,
  required,
  useGetList,
  useNotify,
} from 'react-admin';

import {
  isEmpty,
  isNumber,
} from 'lodash';

import ReactApexChart from 'react-apexcharts';

import {
  Grid,
  Card,
  CardHeader,
  Divider,
  Box,
  LinearProgress,
  useMediaQuery,
} from '@mui/material';

import useDialogStatus from '../../../hooks/useDialogStatus';

import NoResults from '../../layout/NoResults';
import SelectInputButton from '../../layout/inputFields/SelectInputButton';

import ReferenceDetailedTransactionTable from '../ReferenceDetailedTransactionTable';
import Params from '../Params';
import { requestTypes } from '../../../constants/requestTypes';
import theme from '../../../theme';
import { EntityTypes } from '../../../constants/entityTypes';
import SubmitOnChange from '../../utils/SubmitOnChange';

type DataPoint = {
  x: string;
  y: number;
  transactions: unknown[];
  currency: string;
}

type Data = {
  id: Identifier;
  window: {
    from: string;
    to: string;
  };
  max: number;
  min: number;
  totalVolume: number;
  incomingVolume: number;
  outgoingVolume: number;
  incomingFrequency: number;
  totalFrequency: number;
  outgoingFrequency: number;
  transactions: string[];
};

const CHOICES = [
  { id: 'Total Frequency', name: 'Total frequency distribution' },
  { id: 'Outgoing Frequency', name: 'Outgoing frequency distribution' },
  { id: 'Incoming Frequency', name: 'Incoming frequency distribution' },
  { id: 'Total Volume', name: 'Total volume distribution' },
  { id: 'Incoming Volume', name: 'Incoming volume distribution' },
  { id: 'Outgoing Volume', name: 'Outgoing volume distribution' },
];

const withinBucket = (
  value1: number,
  value2: number,
  bucket: number,
) => Math.abs(value1 - value2) <= bucket;

const processData = (
  data: Data[] | undefined,
  prop: keyof Data,
  bucketSize: number,
) => {
  if (isEmpty(data)) return data;

  const combinedData = data!.reduce<{
    x: number;
    y: number;
    transactions: string[];
  }[]>(
    (acc, curr) => {
      const propValue = curr[prop] as number;

      const foundIndex = acc.findIndex(
        (item) => withinBucket(item.x, propValue, bucketSize),
      );

      if (foundIndex !== -1) {
        acc[foundIndex].y += 1;
        acc[foundIndex].transactions = [...acc[foundIndex].transactions, ...curr.transactions];
      } else {
        acc.push({
          x: propValue,
          y: 1,
          transactions: curr.transactions,
        });
      }

      return acc;
    },
    [],
  ).sort((a, b) => a.x - b.x);

  return combinedData;
};

const useGetGraphData = (
  data: Data[] | undefined,
  onClickGraph: (_event: unknown, _chartContext: unknown, config: unknown) => void,
  currency: string,
  bucketWidth: number,
  showSeries: string,
) => {
  const [hasData, setHasData] = useState(false);
  const [options, setOptions] = useState<ReactApexChart['props']['options']>();
  const [series, setSeries] = useState<ReactApexChart['props']['series']>();

  const isLargeScreen = useMediaQuery(theme.breakpoints.up('md'));

  const {
    totalFrequency,
    totalVolume,
    incomingVolume,
    outgoingVolume,
    outgoingFrequency,
    incomingFrequency,
  } = useMemo(() => ({
    totalFrequency: processData(data, 'totalFrequency', bucketWidth),
    incomingFrequency: processData(data, 'incomingFrequency', bucketWidth),
    outgoingFrequency: processData(data, 'outgoingFrequency', bucketWidth),
    totalVolume: processData(data, 'totalVolume', bucketWidth),
    incomingVolume: processData(data, 'incomingVolume', bucketWidth),
    outgoingVolume: processData(data, 'outgoingVolume', bucketWidth),
  }), [bucketWidth, data]);

  useEffect(() => {
    if (isEmpty(data)) {
      setHasData(false);
      setOptions(undefined);
      setSeries(undefined);
      return;
    }

    const newSeries = [{
      name: 'Total Frequency',
      data: totalFrequency,
      color: '#FEB019',
    },
    {
      name: 'Outgoing Frequency',
      data: outgoingFrequency,
      color: '#FEB019',
    },
    {
      name: 'Incoming Frequency',
      data: incomingFrequency,
      color: '#FEB019',
    },
    {
      name: 'Total Volume',
      data: totalVolume,
      color: '#388e3c',
    },
    {
      name: 'Incoming Volume',
      data: incomingVolume,
      color: '#388e3c',
    },
    {
      name: 'Outgoing Volume',
      data: outgoingVolume,
      color: '#388e3c',
    },

    ]?.filter((item) => item.name === showSeries) as ReactApexChart['props']['series'];

    const newOptions = {
      chart: {
        redrawOnParentResize: true,
        stacked: false,
        height: 400,
        events: {
          dataPointSelection: onClickGraph,
        },
        zoom: {
          enabled: true,
        },
        toolbar: {
          show: true,
        },
      },
      plotOptions: {
        bar: {
          columnWidth: isLargeScreen ? '5%' : '10%',
          borderRadius: 8,
          borderRadiusApplication: 'end',
        },
      },
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        y: {
          formatter: undefined,
          title: {
            formatter: () => 'Frequency',
          },
        },
        x: {
          formatter(_value: number, {
            seriesIndex, w, dataPointIndex,
          }: {
            seriesIndex: number;
            dataPointIndex: number;
            w: RaRecord;
          }) {
            const {
              name,
            } = w.globals.initialSeries[seriesIndex];

            const {
              x,
            } = w.globals.initialSeries[seriesIndex].data[dataPointIndex];

            return `${name} of ${x}`;
          },
        },
      },
      legend: {
        show: false,
      },
      yaxis: {
        decimalsInFloat: 0,
        title: {
          text: 'Frequency',
        },
      },
      xaxis: {
        tickAmount: 'dataPoints',
        tickPlacement: 'on',
        type: 'numeric',
        decimalsInFloat: 0,
        labels: {
          rotate: -90,
        },
      },
    } as ReactApexChart['props']['options']; // satisfies ReactApexChart['props']['options'] when ts is upgraded;

    setHasData(true);
    setOptions(newOptions);
    setSeries(newSeries);
  }, [
    currency,
    data,
    incomingVolume,
    onClickGraph,
    outgoingVolume,
    showSeries,
    totalFrequency,
    totalVolume,
    outgoingFrequency,
    incomingFrequency,
    isLargeScreen,
  ]);

  return {
    hasData,
    options,
    series,
  };
};

const Distribution = ({
  params,
  setParams,
  filter,
}: {
  params: Params;
  setParams: React.Dispatch<React.SetStateAction<Params>>;
  filter: {
    from: string;
    to: string;
    currency: string;
    entityId: string;
    entityType: EntityTypes;
    requestType: typeof requestTypes.TRANSACTION;
    transactionTypeConfiguration?: string;
  };
}) => {
  const notify = useNotify();

  const { open, closeDialog, openDialog } = useDialogStatus();

  const [bucketWidth, setBucketWidth] = useState(0);

  const [selectedData, setData] = useState<DataPoint>();
  const [showSeries, setShowSeries] = useState('Total Volume');

  const { data, isLoading } = useGetList<Data>(
    'requests/graph/distribution',
    {
      filter: {
        ...filter,
        windowSize: params.distributionWindow,
      },
    },
    {
      enabled: isNumber(params.distributionWindow),
      onError: (e) => {
        notify(e instanceof HttpError && e.status === 400 ? e.message : 'Could not get distribution data', { type: 'error' });
      },
    },
  );

  const onClickGraph = useCallback((
    _event: any,
    _chartContext: any,
    config: any,
  ) => {
    const dataPoint = config?.w?.config?.series
      .find((series: RaRecord) => series.data.length > 0)
      ?.data[config?.dataPointIndex];
    setData(() => dataPoint);
    openDialog();
  }, [openDialog]);

  const { hasData, options, series } = useGetGraphData(
    data,
    onClickGraph,
    params.currency,
    bucketWidth,
    showSeries,
  );

  const onSubmit = useCallback((values: {
    distributionWindow: number;
    bucketWidth: number;
  }) => {
    setParams((previousParams) => ({
      ...previousParams,
      distributionWindow: values.distributionWindow,
    }));
    setBucketWidth(values.bucketWidth);
  }, [setParams]);

  if (isLoading) return <LinearProgress />;

  return (
    <Grid container spacing={6}>
      <Grid size={12}>
        <Card variant="outlined">
          <CardHeader
            title={(
              <Box maxWidth="fit-content">
                <SelectInputButton
                  choices={CHOICES}
                  value={showSeries}
                  setValue={setShowSeries}
                  size="large"
                  color="secondary"
                  sx={{ justifyContent: 'flex-start' }}
                />
              </Box>
            )}
            action={(
              <Form record={{ ...params, bucketWidth }}>
                <Box gap="1rem" display="flex" flexDirection="row">
                  <NumberInput
                    label="Window"
                    source="distributionWindow"
                    validate={[required(), minValue(0)]}
                  />
                  <NumberInput
                    label="Bucket width"
                    source="bucketWidth"
                    validate={[required(), minValue(0)]}
                  />
                  <SubmitOnChange onSubmit={onSubmit} />
                </Box>
              </Form>
            )}
          />
          <Divider />
          {hasData && <ReactApexChart options={options} series={series} type="bar" height={600} />}
          {!hasData && <NoResults variant="h4" />}
        </Card>
      </Grid>
      <ReferenceDetailedTransactionTable
        open={open}
        closeDialog={closeDialog}
        data={selectedData}
      />
    </Grid>
  );
};

export default Distribution;
